export type HyperpayConfigShape = {
  checkoutMode?: "unified" | "split";
  checkoutId?: string;
  checkoutIdCard?: string;
  checkoutIdApplePay?: string;
  widgetBaseUrl?: string;
  return_url?: string;
  checkoutIntegrity?: string | null;
  checkoutIntegrityCard?: string | null;
  checkoutIntegrityApplePay?: string | null;
  wpwlOptions?: Record<string, unknown>;
  checkoutError?: string;
};

export function resolveHyperPayCheckoutMode(
  hyperpayConfig: HyperpayConfigShape | null | undefined
): "unified" | "split" {
  return hyperpayConfig?.checkoutMode === "split" ? "split" : "unified";
}

/**
 * Backend returned hyperpay → resolve mode and validate payload.
 * Returns null when hyperpay config is missing or incomplete for the chosen mode.
 */
export function getHyperPayCheckout(
  hyperpayConfig: HyperpayConfigShape | null | undefined
): { mode: "unified" | "split"; config: HyperpayConfigShape } | null {
  if (!hyperpayConfig) return null;

  if (!hyperpayConfig.widgetBaseUrl || !hyperpayConfig.return_url) return null;

  const mode = resolveHyperPayCheckoutMode(hyperpayConfig);

  if (mode === "split") {
    if (hyperpayConfig.checkoutIdCard && hyperpayConfig.checkoutIdApplePay) {
      return { mode: "split", config: hyperpayConfig };
    }
    // Partial split (one channel failed) — still show the working widget
    const checkoutId =
      hyperpayConfig.checkoutIdCard ?? hyperpayConfig.checkoutIdApplePay;
    if (!checkoutId) return null;
    return {
      mode: "unified",
      config: {
        ...hyperpayConfig,
        checkoutId,
        checkoutIntegrity:
          (hyperpayConfig.checkoutIdCard
            ? hyperpayConfig.checkoutIntegrityCard
            : hyperpayConfig.checkoutIntegrityApplePay) ??
          hyperpayConfig.checkoutIntegrity ??
          null,
      },
    };
  }

  const checkoutId =
    hyperpayConfig.checkoutId ??
    hyperpayConfig.checkoutIdCard ??
    hyperpayConfig.checkoutIdApplePay;
  if (!checkoutId) return null;

  return {
    mode: "unified",
    config: {
      ...hyperpayConfig,
      checkoutId,
      checkoutIntegrity:
        hyperpayConfig.checkoutIntegrity ??
        hyperpayConfig.checkoutIntegrityCard ??
        hyperpayConfig.checkoutIntegrityApplePay ??
        null,
    },
  };
}

/** Backend `hyperpay.checkoutError` — not on resolved config when getHyperPayCheckout returns null. */
export function getHyperPayCheckoutError(
  hyperpayConfig: HyperpayConfigShape | null | undefined
): string | null {
  const msg = hyperpayConfig?.checkoutError;
  return msg ? String(msg) : null;
}
